import axios, { CancelToken } from 'axios'
import type { ActionTree, MutationTree } from 'vuex'
import IApiBaseService from '@/services/interfaces/IApiBase.service'
import { IBaseModuleState } from '@/store/interfaces/IBase.module'
import { IFilter, IRequestPayload, IResponseException, ISort } from '@/types'
import { PageSize, ShowLoadingAfterMs } from '@/constants'

export function createBaseException(): IResponseException {
  return {
    exceptionMessage: '',
    validationErrors: [],
  }
}

export function createBaseState<TEntity, TData>(
  service: IApiBaseService<TEntity, TData>,
): IBaseModuleState<TEntity, TData> {
  return {
    data: undefined,
    service,
    loading: false,
    validationErrors: [],
    responseException: createBaseException(),
  }
}

export function createBaseMutations<TEntity, TData>(): MutationTree<IBaseModuleState<TEntity, TData>> {
  return {
    setData(state, data: TData) {
      state.data = data
    },
    setLoading(state, loading: boolean) {
      state.loading = loading
    },
    setResponseException(state, exception: IResponseException) {
      state.responseException = exception
      state.validationErrors = exception.validationErrors
    },
  }
}

export function createBaseActions<TEntity, TData>(): ActionTree<IBaseModuleState<TEntity, TData>, any> {
  return {
    async fetchData({ commit, state }) {
      try {
        commit('setLoading', true)
        const response: TData = await state.service.getAll()
        commit('setData', response)
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchDataPaged({ commit, state }, payload: {
      pageNumber: number
      searchQuery: string | undefined
      searchType: string | undefined
      filters: IFilter[]
      sortBy: ISort | undefined
    }) {
      const timer = setTimeout(() => commit('setLoading', true), ShowLoadingAfterMs)

      try {
        const requestPayload: Record<string, unknown> = {
          pageNumber: payload.pageNumber,
          pageSize: PageSize,
        }

        if (payload.searchQuery) {
          requestPayload.searchQuery = payload.searchQuery
          requestPayload.searchType = payload.searchType
        }

        payload.filters.forEach((filter: IFilter) => {
          if (filter.value) {
            requestPayload[filter.key] = filter.value
          }
        })

        if (payload.sortBy?.sortField) {
          requestPayload.sortField = payload.sortBy.sortField
          requestPayload.sortOrder = payload.sortBy.sortOrder
        }

        const response: TData = await state.service.getAll('', requestPayload)
        commit('setData', response)
      } finally {
        clearTimeout(timer)
        commit('setLoading', false)
      }
    },

    async fetchDataPagedWithCancelToken({ commit, state }, payload: {
      pageNumber: number
      searchQuery: string | undefined
      searchType: string | undefined
      filters: IFilter[]
      sortBy: ISort | undefined
      cancelToken: CancelToken
    }) {
      const timer = setTimeout(() => commit('setLoading', true), ShowLoadingAfterMs)

      try {
        const requestPayload: Record<string, unknown> = {
          pageNumber: payload.pageNumber,
          pageSize: PageSize,
        }

        if (payload.searchQuery) {
          requestPayload.searchQuery = payload.searchQuery
          requestPayload.searchType = payload.searchType
        }

        payload.filters.forEach((filter: IFilter) => {
          if (filter.value) {
            requestPayload[filter.key] = filter.value
          }
        })

        if (payload.sortBy?.sortField) {
          requestPayload.sortField = payload.sortBy.sortField
          requestPayload.sortOrder = payload.sortBy.sortOrder
        }

        const response: TData = await state.service.getAllWithCancelToken(payload.cancelToken, '', requestPayload)
        commit('setData', response)
      } catch (error) {
        if (!axios.isCancel(error)) throw error
      } finally {
        clearTimeout(timer)
        commit('setLoading', false)
      }
    },

    async fetchDataFiltered({ commit, state }, filter: string) {
      const timer = setTimeout(() => commit('setLoading', true), ShowLoadingAfterMs)

      try {
        const response: TData = await state.service.getAll('', { searchQuery: filter })
        commit('setData', response)
      } finally {
        clearTimeout(timer)
        commit('setLoading', false)
      }
    },

    async get({ commit, state }, id: number): Promise<TEntity> {
      try {
        commit('setLoading', true)
        return await state.service.getById(id)
      } finally {
        commit('setLoading', false)
      }
    },

    async add({ commit, dispatch, state }, payload: IRequestPayload<TEntity>): Promise<TEntity> {
      try {
        commit('setLoading', true)
        const response: TEntity = await state.service.post(payload.entity, payload.path)
        await dispatch('fetchData')
        return response
      } catch (error) {
        commit('setResponseException', error as IResponseException)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async update({ commit, dispatch, state }, payload: IRequestPayload<TEntity>): Promise<TEntity> {
      try {
        commit('setLoading', true)
        const response: TEntity = await state.service.put(payload.entity, payload.path)
        await dispatch('fetchData')
        return response
      } catch (error) {
        commit('setResponseException', error as IResponseException)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async delete({ commit, dispatch, state }, id: number): Promise<boolean> {
      try {
        commit('setLoading', true)
        const response = await state.service.delete(id)
        await dispatch('fetchData')
        return response
      } finally {
        commit('setLoading', false)
      }
    },
  }
}
